import React from 'react'
import { TouchableOpacity, View, StyleProp, ViewStyle, useWindowDimensions } from 'react-native'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Pill, PillProps, Text } from '@components/base'
import { Benefit, CircularCheck } from '@components'
import { GroupableProduct, isProduct } from '../types/types'
import { getPriceRangeDescriptor } from '../utils/utils'

export interface SubscriptionItemProps<T extends GroupableProduct> {
  title: string
  description?: string | null
  details?: string[] | null
  item: T
  savings?: number
  style?: StyleProp<ViewStyle>
  onItemPress: (item: T) => void
  ctaText?: string
  insuranceCoverageAvailable?: boolean
  selectMode?: boolean
  selected?: boolean
  showPrice?: boolean
  pill?: {
    text: string
    fill?: PillProps['fill'] | null
    size?: PillProps['size'] | null
    type?: PillProps['status'] | null
  } | null
}

export const SubscriptionItem = <T extends GroupableProduct>({
  title,
  description,
  details,
  item,
  savings = 0,
  style,
  onItemPress,
  ctaText,
  insuranceCoverageAvailable = false,
  selectMode = false,
  selected = false,
  showPrice = true,
  pill: pillFromProps,
}: SubscriptionItemProps<T>) => {
  const styles = useStyleSheet(themedStyle)
  const products = isProduct(item) ? [item] : item.products
  const amount = isProduct(item) ? item.price : 0
  const recurring = products.some((product) => product.recurring)
  const priceDescriptor = getPriceRangeDescriptor(item)
  const dimensions = useWindowDimensions()
  const hasNarrowDevice = dimensions.width < 390

  const pill = pillFromProps || (isProduct(item) ? item.pill : undefined)

  const handlePress = () => {
    onItemPress(item)
  }

  return (
    <TouchableOpacity
      style={[styles.container, selected && styles.containerSelected, style]}
      activeOpacity={0.7}
      onPress={handlePress}
      accessibilityLabel={title}
    >
      <View style={styles.row}>
        {hasNarrowDevice ? (
          <Text type="regular" bold style={styles.title}>
            {title}
          </Text>
        ) : (
          <Text type="title-3" style={styles.title}>
            {title}
          </Text>
        )}
        {selectMode ? (
          <CircularCheck selected={selected} />
        ) : (
          <Icon name="caret-right" style={styles.icon} />
        )}
      </View>
      {!!description && (
        <Text type="regular" style={styles.description}>
          {description}
        </Text>
      )}
      {details && (
        <View style={styles.details}>
          {details.map((detail) => (
            <Benefit key={detail} benefit={detail} />
          ))}
        </View>
      )}
      <View style={styles.priceContainer}>
        {showPrice && (
          <View style={styles.priceDescription}>
            <View style={styles.priceRow}>
              <View>
                {!!priceDescriptor.prefix && <Text type="small">{priceDescriptor.prefix}</Text>}
                {recurring ? (
                  <Text type="large" bold>
                    {priceDescriptor.amount}
                    <Text type="small" bold>
                      {priceDescriptor.suffix}
                    </Text>
                  </Text>
                ) : (
                  <View style={styles.priceRow}>
                    <Text type="regular" bold style={styles.priceWithoutDiscount}>
                      {priceDescriptor.amount}
                      <Text type="small" bold>
                        {priceDescriptor.suffix}
                      </Text>
                    </Text>
                    {savings > 0 && (
                      <Text type="large" bold>
                        {`$${amount - savings}`}
                      </Text>
                    )}
                  </View>
                )}
              </View>
              {ctaText && (
                <View style={styles.cta}>
                  <Button
                    type="primary"
                    size="small"
                    onPress={handlePress}
                    accessibilityLabel={ctaText}
                  >
                    {ctaText}
                  </Button>
                </View>
              )}
            </View>
            {insuranceCoverageAvailable && (
              <View style={styles.insuranceNotice}>
                <Text type="small" bold style={styles.saveText}>
                  Free 60 or 90 minute call if eligible
                </Text>
              </View>
            )}
          </View>
        )}
        {pill && (
          <Pill
            text={pill.text}
            fill={pill.fill || undefined}
            size={pill.size || undefined}
            status={(pill.type as any) || undefined}
            style={styles.tag}
          />
        )}
      </View>
    </TouchableOpacity>
  )
}

const themedStyle = StyleService.create({
  container: {
    minHeight: 180,
    padding: 16,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: 'theme.surface.base1',
    backgroundColor: 'theme.surface.base3',
  },
  containerSelected: {
    borderColor: 'theme.success.base',
  },
  row: {
    flexDirection: 'row',
  },
  title: {
    flex: 1,
    marginBottom: 4,
  },
  icon: {
    width: 16,
    height: 16,
    marginLeft: 16,
    color: 'theme.primary.base',
  },
  description: {
    marginTop: 4,
    marginBottom: 8,
  },
  details: {
    marginBottom: 26,
  },
  priceContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cta: {
    marginLeft: 24,
  },
  priceWithoutDiscount: {
    textDecorationLine: 'theme.surface.base',
    marginRight: 6,
  },
  priceDescription: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  insuranceNotice: {
    height: 32,
    backgroundColor: 'theme.accent.citron',
    justifyContent: 'center',
    borderRadius: 16,
    paddingHorizontal: 16,
    marginLeft: 10,
  },
  saveText: {
    color: 'theme.solid.black',
  },
  tag: {
    alignSelf: 'flex-start',
    marginTop: 8,
    minWidth: 80,
  },
})
